import React from 'react';
import { Routes, Route, Link, Navigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { UserMenu } from '../components/Layout/UserMenu';
import { AppHeader } from '../components/Layout/AppHeader';
import { MembersManager } from '../components/Settings/MembersManager';
import { ComponentDemo } from './admin/ComponentDemo';
import { OrgsList } from './admin/OrgsList';
import { UsersList } from './admin/UsersList';
import { OrgDetails } from './admin/OrgDetails';
import { AdminDashboard } from './admin/AdminDashboard';
import { InvitesList } from './admin/InvitesList';
import { LLMUsage } from './admin/LLMUsage';
import { LLMLogs } from './admin/LLMLogs';
import { LLMPlayground } from './admin/LLMPlayground';
import { JobsDashboard } from './admin/JobsDashboard';
import { JobsList } from './admin/JobsList';
import { ScheduledJobs } from './admin/ScheduledJobs';
import { AdminBillingConsole } from './admin/billing/AdminBillingConsole';
import { SidebarLayout } from '../components/Layout/SidebarLayout';
import { Logo } from '../components/ui/Logo';
import { OrgOverview } from './organization/OrgOverview';
import { MembersAndInvites } from './organization/MembersAndInvites';
import { OrgGallery } from './organization/OrgGallery';
import { AboutCompany } from './organization/AboutCompany';
import { OrgCanvases } from './organization/OrgCanvases';
import { Onboarding } from './Onboarding';
import { ProfilePage } from './settings/Account/ProfilePage';
import { SecurityPage } from './settings/Account/SecurityPage';
import { SessionsPage } from './settings/Account/SessionsPage';
import { DangerPage } from './settings/Account/DangerPage';
import { CanvasPage } from './canvas/CanvasPage';
import { SharedCanvasPage } from './canvas/SharedCanvasPage';
import { DashboardPage } from './dashboard/DashboardPage';
import { TicketsList } from './admin/feedback/TicketsList';
import { TicketDetail } from './admin/feedback/TicketDetail';
import { KnowledgeBase } from './admin/feedback/KnowledgeBase';
import { KnowledgeArticleEditor } from './admin/feedback/KnowledgeArticleEditor';
import { FeedbackSettings } from './admin/feedback/FeedbackSettings';
import { FeedbackButton } from '../components/Feedback/FeedbackButton';
import { FeedbackModal } from '../components/Feedback/FeedbackModal';
import { BlogPostsList } from './admin/blog/BlogPostsList';
import { BlogPostEditor } from './admin/blog/BlogPostEditor';
import { BlogCategories } from './admin/blog/BlogCategories';
import { AdminMediaLibrary } from './admin/media/AdminMediaLibrary';
import { CanvasTemplates } from './admin/CanvasTemplates';
import { UseCases } from './admin/UseCases';
import { TemplatesPage } from './templates/TemplatesPage';
import { Integrations } from './organization/Integrations';
import { Credits } from './organization/Credits';
import { OrgBilling } from './organization/OrgBilling';
import { AdminCreditGrants } from './admin/AdminCreditGrants';
import { PlanFeaturesAdmin } from './admin/billing/PlanFeaturesAdmin';
import { PaywallModal } from '../components/Paywall/PaywallModal';
import { ExhaustedTrialModal } from '../components/Paywall/ExhaustedTrialModal';
import { EmailVerificationBanner } from '../components/Layout/EmailVerificationBanner';
import { useCreditStore } from '../stores/creditStore';
import { usePaywallStore } from '../stores/paywallStore';

export function AppRouter() {
  // Post-checkout return handler
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      // Remove query param from URL without reload
      const url = new URL(window.location.href);
      url.searchParams.delete('checkout');
      window.history.replaceState({}, '', url.pathname + url.search);

      // Refresh credits (webhook auto-grants credits on successful payment)
      useCreditStore.getState().reset();

      // Close paywall if open and unlock canvas
      usePaywallStore.getState().closePaywall();
      usePaywallStore.getState().setCanvasLocked(false);
      usePaywallStore.getState().resetCanvasDismissed();
    }
  }, []);

  return (
    <>
      {/* Persistent banner for unverified email — cannot be dismissed */}
      <EmailVerificationBanner />
      {/* Global PaywallModal — rendered at app root, triggered from anywhere */}
      <PaywallModal />
      <ExhaustedTrialModal />
      <Routes>
        <Route path="/dashboard" element={<DashboardLayout />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/canvas/:id" element={<CanvasPage />} />
        <Route path="/s/:token" element={<SharedCanvasPage />} />
        <Route path="/admin" element={<SidebarLayout type="admin"><SuperAdminGuard><AdminDashboard /></SuperAdminGuard></SidebarLayout>} />
        <Route path="/admin/orgs" element={<SidebarLayout type="admin"><SuperAdminGuard><OrgsList /></SuperAdminGuard></SidebarLayout>} />
        <Route path="/admin/orgs/:id" element={<SidebarLayout type="admin"><SuperAdminGuard><OrgDetails /></SuperAdminGuard></SidebarLayout>} />
        <Route path="/admin/users" element={<SidebarLayout type="admin"><SuperAdminGuard><UsersList /></SuperAdminGuard></SidebarLayout>} />
        <Route path="/admin/invites" element={<SidebarLayout type="admin"><SuperAdminGuard><InvitesList /></SuperAdminGuard></SidebarLayout>} />
        <Route path="/admin/styleguide" element={<SidebarLayout type="admin"><SuperAdminGuard><ComponentDemo /></SuperAdminGuard></SidebarLayout>} />
        <Route path="/admin/llm/usage" element={<SidebarLayout type="admin"><SuperAdminGuard><LLMUsage /></SuperAdminGuard></SidebarLayout>} />
        <Route path="/admin/llm/logs" element={<SidebarLayout type="admin"><SuperAdminGuard><LLMLogs /></SuperAdminGuard></SidebarLayout>} />
        <Route path="/admin/llm/playground" element={<SidebarLayout type="admin"><SuperAdminGuard><LLMPlayground /></SuperAdminGuard></SidebarLayout>} />
        <Route path="/admin/jobs" element={<SidebarLayout type="admin"><SuperAdminGuard><JobsDashboard /></SuperAdminGuard></SidebarLayout>} />
        <Route path="/admin/jobs/list" element={<SidebarLayout type="admin"><SuperAdminGuard><JobsList /></SuperAdminGuard></SidebarLayout>} />
        <Route path="/admin/jobs/scheduled" element={<SidebarLayout type="admin"><SuperAdminGuard><ScheduledJobs /></SuperAdminGuard></SidebarLayout>} />
        <Route path="/admin/billing" element={<SidebarLayout type="admin"><SuperAdminGuard><AdminBillingConsole /></SuperAdminGuard></SidebarLayout>} />
        <Route path="/admin/billing/features" element={<SidebarLayout type="admin"><SuperAdminGuard><PlanFeaturesAdmin /></SuperAdminGuard></SidebarLayout>} />
        <Route path="/admin/credits" element={<SidebarLayout type="admin"><SuperAdminGuard><AdminCreditGrants /></SuperAdminGuard></SidebarLayout>} />
        <Route path="/admin/feedback" element={<SidebarLayout type="admin"><TicketsList /></SidebarLayout>} />
        <Route path="/admin/feedback/settings" element={<SidebarLayout type="admin"><FeedbackSettings /></SidebarLayout>} />
        <Route path="/admin/feedback/:id" element={<SidebarLayout type="admin"><TicketDetail /></SidebarLayout>} />
        <Route path="/admin/blog" element={<SidebarLayout type="admin"><BlogPostsList /></SidebarLayout>} />
        <Route path="/admin/blog/new" element={<SidebarLayout type="admin"><BlogPostEditor /></SidebarLayout>} />
        <Route path="/admin/blog/categories" element={<SidebarLayout type="admin"><BlogCategories /></SidebarLayout>} />
        <Route path="/admin/blog/:id" element={<SidebarLayout type="admin"><BlogPostEditor /></SidebarLayout>} />
        <Route path="/admin/media" element={<SidebarLayout type="admin"><AdminMediaLibrary /></SidebarLayout>} />
        <Route path="/admin/knowledge" element={<SidebarLayout type="admin"><KnowledgeBase /></SidebarLayout>} />
        <Route path="/admin/knowledge/new" element={<SidebarLayout type="admin"><KnowledgeArticleEditor /></SidebarLayout>} />
        <Route path="/admin/knowledge/:id" element={<SidebarLayout type="admin"><KnowledgeArticleEditor /></SidebarLayout>} />
        <Route path="/admin/canvas-templates" element={<SidebarLayout type="admin"><SuperAdminGuard><CanvasTemplates /></SuperAdminGuard></SidebarLayout>} />
        <Route path="/admin/use-cases" element={<SidebarLayout type="admin"><SuperAdminGuard><UseCases /></SuperAdminGuard></SidebarLayout>} />
        <Route path="/organization" element={<SidebarLayout type="organization"><OrgOverview /></SidebarLayout>} />
        <Route path="/organization/members" element={<SidebarLayout type="organization"><MembersAndInvites /></SidebarLayout>} />
        <Route path="/organization/gallery" element={<SidebarLayout type="organization"><OrgGallery /></SidebarLayout>} />
        <Route path="/organization/canvases" element={<SidebarLayout type="organization"><OrgCanvases /></SidebarLayout>} />
        <Route path="/organization/about" element={<SidebarLayout type="organization"><AboutCompany /></SidebarLayout>} />
        <Route path="/organization/integrations" element={<SidebarLayout type="organization"><Integrations /></SidebarLayout>} />
        <Route path="/organization/credits" element={<SidebarLayout type="organization"><Credits /></SidebarLayout>} />
        <Route path="/organization/billing" element={<SidebarLayout type="organization"><OrgBilling /></SidebarLayout>} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/settings/account" element={<SidebarLayout type="account"><ProfilePage /></SidebarLayout>} />
        <Route path="/settings/account/profile" element={<SidebarLayout type="account"><ProfilePage /></SidebarLayout>} />
        <Route path="/settings/account/security" element={<SidebarLayout type="account"><SecurityPage /></SidebarLayout>} />
        <Route path="/settings/account/sessions" element={<SidebarLayout type="account"><SessionsPage /></SidebarLayout>} />
        <Route path="/settings/account/danger" element={<SidebarLayout type="account"><DangerPage /></SidebarLayout>} />
        <Route path="/settings/members" element={<AdminPageWithHeader><MembersManager /></AdminPageWithHeader>} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      <FeedbackButton />
    </>
  );
}

/** Redirects non-super-admin users to the first allowed admin page */
function SuperAdminGuard({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = !!(user as any)?.isSuperAdmin;
  if (!isSuperAdmin) {
    return <Navigate to="/admin/blog" replace />;
  }
  return <>{children}</>;
}

function DashboardLayout() {
  const [showFeedback, setShowFeedback] = React.useState(false);
  return (
    <div className="min-h-screen flex flex-col">
      <AppHeader
        rightExtra={
          <button
            onClick={() => setShowFeedback(true)}
            className="hidden sm:inline text-sm text-gray-500 hover:text-violet-600 transition-colors"
          >
            Need Help?
          </button>
        }
      />
      <DashboardPage />
      {showFeedback && <FeedbackModal onClose={() => setShowFeedback(false)} />}
    </div>
  );
}


function AdminPageWithHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col">
      <div className="h-12 bg-white border-b flex items-center justify-between px-4 flex-shrink-0">
        <Link to="/dashboard" className="flex items-center hover:opacity-80 transition-opacity">
          <Logo size="sm" />
          <span className="ml-2 text-xs font-medium text-gray-400 uppercase tracking-wider">Admin</span>
        </Link>
        <UserMenu />
      </div>
      <div className="flex-1 overflow-auto">
        {children}
      </div>
    </div>
  );
}
